import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '@lipaflow/shared'
import toast from 'react-hot-toast'
import { ExclamationTriangleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline'
import type { StepProps } from '../types'
import { sectorPath, useSector } from '../../../sector/sector'

/** Step 7: go live, once nothing stops it (the server's go-live checks, shown before trying). */
export default function CompleteStep({ tenantId, blockers }: StepProps & { blockers: string[] }) {
  const navigate = useNavigate()
  const sector = useSector()
  const queryClient = useQueryClient()

  const completeMutation = useMutation({
    mutationFn: async () => {
      await api.post('/admin/wizard/complete', { tenant_id: tenantId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      toast.success('The customer is live')
      navigate(sectorPath(sector.code, 'customers'))
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to go live'))
    },
  })

  return (
    <div className="text-center">
      <RocketLaunchIcon className="w-16 h-16 text-accent mx-auto mb-4" />
      {blockers.length > 0 ? (
        <>
          <h2 className="text-2xl font-bold mb-2">Not ready to go live yet</h2>
          <ul className="mx-auto mb-6 max-w-lg space-y-2 text-left">
            {blockers.map((b) => (
              <li key={b} className="flex gap-2 rounded-button bg-warning-soft p-3 text-body-sm text-ink">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-warning" aria-hidden />
                {b}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold mb-2">Ready to go live</h2>
          <p className="text-ink-muted mb-6">
            Every step is done. Going live starts their 14-day trial.
          </p>
        </>
      )}
      <button
        onClick={() => completeMutation.mutate()}
        disabled={completeMutation.isPending || blockers.length > 0}
        className="px-8 py-3 bg-accent text-on-accent rounded-button hover:bg-accent-hover disabled:opacity-50 text-lg"
      >
        {completeMutation.isPending ? 'Going live…' : 'Go live'}
      </button>
    </div>
  )
}
